// =============================================================================
//  useScans.ts - hybride live/demo
//
//  - En live  : useQuery sur les endpoints REST
//  - En demo  : useSyncExternalStore sur le scanStore (localStorage)
//
//  API publique stable entre les modes : useAllScans, useScansForTenant,
//  useLatestScan, useScanById.
//
//  IMPORTANT : on appelle TOUJOURS les 2 versions (live + demo) - regles de
//  hooks React interdisent les calls conditionnels. La query live est
//  desactivee via `enabled: IS_LIVE_MODE` quand on est en demo, donc no-op.
// =============================================================================

import { useSyncExternalStore } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  getLatestScan,
  listScans,
  listScansForTenant,
  subscribeScans,
} from '../domain/scanStore';
import { IS_LIVE_MODE } from '../api/liveMode';
import { scanApi, type ScanDoc } from '../api/scanApi';
import { Scan } from '../domain/types';

/**
 * Mapper ScanDoc (API self-scan, PascalCase) -> Scan (type domain frontend, camelCase).
 * Le backend renvoie les checks avec ControlId/Status/Score/EvaluatedAt (style PS).
 * Le type domain attend controlId/status/score/evaluatedAt (style TS) pour matcher
 * computeScoreBreakdown et les composants graph/dashboard.
 */
function mapScanDocToScan(d: ScanDoc): Scan {
  // Mappe chaque result PascalCase -> camelCase
  const mappedResults = (d.results ?? []).map((r) => {
    const raw = r as unknown as Record<string, unknown>;
    // Preuves allegees stockees inline par RunScan (v2026.07). Retro-compat :
    // on lit Evidences (PascalCase worker) ou evidences (deja mappe).
    const evidences = (raw.Evidences ?? raw.evidences ?? []) as unknown[];
    const evidenceCount = Number(raw.EvidenceCount ?? raw.evidenceCount ?? (Array.isArray(evidences) ? evidences.length : 0));
    return {
      controlId:   r.ControlId,
      status:      normalizeStatus(r.Status),
      score:       typeof r.Score === 'number' ? r.Score : Number(r.Score ?? 0),
      message:     r.Message ?? '',
      // i18n : passe-plat des champs MessageKey/MessageVars du worker.
      // L UI les utilise via getResultMessage() pour afficher dans la langue active.
      messageKey:  r.MessageKey,
      messageVars: r.MessageVars,
      evaluatedAt: r.EvaluatedAt ?? '',
      evidences,
      evidenceCount,
    };
  });

  return {
    id:         d.id,
    tenantId:   d.clientId,
    startedAt:  d.startedAt,
    finishedAt: d.finishedAt ?? '',
    results:    mappedResults as unknown as Scan['results'],
    globalScore: typeof d.globalScore === 'number' ? d.globalScore : undefined,
    meta: {
      triggeredBy:    d.triggeredBy,
      scanType:       d.scanType as 'full' | 'partial',
      catalogVersion: d.meta?.catalogVersion ?? 'unknown',
    },
  };
}

/**
 * Normalise le status backend (PowerShell) vers le type CheckStatus du domain
 * frontend. Accepte les variantes ok/ko/warn/skipped.
 *
 * Mapping :
 *   pass | ok           -> pass
 *   fail | ko           -> fail
 *   warn | warning      -> warning
 *   manual | skipped    -> manual
 *   not_applicable      -> not_applicable
 *   error               -> error
 */
function normalizeStatus(s: string): Scan['results'][number]['status'] {
  const v = (s ?? '').toLowerCase();
  if (v === 'pass' || v === 'ok')              return 'pass';
  if (v === 'fail' || v === 'ko')              return 'fail';
  if (v === 'warn' || v === 'warning')         return 'warning';
  if (v === 'manual' || v === 'skipped')       return 'manual';
  if (v === 'not_applicable' || v === 'n/a')   return 'not_applicable';
  if (v === 'error')                           return 'error';
  // Default safe : on tombe sur manual pour eviter de polluer les counts pass/fail
  return 'manual';
}

// ============================================================================
//  Demo helpers (useSyncExternalStore sur scanStore localStorage)
// ============================================================================

function useAllScansDemo(): Scan[] {
  return useSyncExternalStore(subscribeScans, listScans, listScans);
}

function useScansForTenantDemo(tenantId: string): Scan[] {
  return useSyncExternalStore(
    subscribeScans,
    () => listScansForTenant(tenantId),
    () => listScansForTenant(tenantId),
  );
}

function useLatestScanDemo(tenantId: string): Scan | null {
  return useSyncExternalStore(
    subscribeScans,
    () => getLatestScan(tenantId),
    () => getLatestScan(tenantId),
  );
}

// ============================================================================
//  Live helpers (TanStack Query)
// ============================================================================

function useScansForTenantLive(tenantId: string): Scan[] {
  // Archi B : un user n a acces qu a SON propre client (filtre par clientId JWT).
  // Donc tenantId est ignore - on utilise le meme endpoint que LiveScanPanel pour
  // garantir la coherence d affichage (sinon LiveScanPanel voyait le scan mais
  // pas useLatestScan).
  const q = useQuery({
    queryKey:  ['scans-self'],
    queryFn:   () => scanApi.list(50),
    enabled:   IS_LIVE_MODE && !!tenantId,
    staleTime: 15_000,
  });
  return (q.data?.items ?? []).map(mapScanDocToScan);
}

function useLatestScanLive(tenantId: string): Scan | null {
  const scans = useScansForTenantLive(tenantId);
  return scans[0] ?? null;
}

/**
 * En live archi B, un user n a acces qu a SON client - useAllScansLive est en
 * pratique equivalent a useScansForTenantLive. Plus de boucle sur clients.
 */
function useAllScansLive(): Scan[] {
  const q = useQuery({
    queryKey:  ['scans-self'],
    queryFn:   () => scanApi.list(50),
    enabled:   IS_LIVE_MODE,
    staleTime: 15_000,
  });
  return (q.data?.items ?? [])
    .map(mapScanDocToScan)
    .sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime());
}

// ============================================================================
//  Hooks publics (hybrides)
// ============================================================================

export function useAllScans(): Scan[] {
  const liveScans = useAllScansLive();
  const demoScans = useAllScansDemo();
  return IS_LIVE_MODE ? liveScans : demoScans;
}

export function useScansForTenant(tenantId: string): Scan[] {
  const liveScans = useScansForTenantLive(tenantId);
  const demoScans = useScansForTenantDemo(tenantId);
  return IS_LIVE_MODE ? liveScans : demoScans;
}

export function useLatestScan(tenantId: string): Scan | null {
  const liveScan = useLatestScanLive(tenantId);
  const demoScan = useLatestScanDemo(tenantId);
  return IS_LIVE_MODE ? liveScan : demoScan;
}

/** Recupere un scan par id (utile pour ScanDetailPage). */
export function useScanById(scanId: string | undefined): Scan | null {
  const demoScan = useSyncExternalStore(
    subscribeScans,
    () => (scanId ? listScans().find((s) => s.id === scanId) ?? null : null),
    () => (scanId ? listScans().find((s) => s.id === scanId) ?? null : null),
  );

  // Fetch direct via GET /scans/{id} - plus robuste qu un filtre dans la liste
  // (le scan peut etre > 50 scans en arriere et serait introuvable autrement).
  // L endpoint backend GetScanSelf retourne le doc complet avec ses results.
  const liveQ = useQuery({
    queryKey:  ['scan', scanId],
    queryFn:   () => scanId ? scanApi.get(scanId) : Promise.resolve(null),
    enabled:   IS_LIVE_MODE && !!scanId,
    staleTime: 5_000,
    retry:     false,
  });

  return IS_LIVE_MODE
    ? (liveQ.data ? mapScanDocToScan(liveQ.data) : null)
    : demoScan;
}
